import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useCommunity } from "contexts/community";

import api from "@helpers/api";
import { adaptMyEnrollmentDetail } from "../../helpers/apiAdapters";

/**
 * Creator — attach a community to an enrollment (§6.10). Owns the enrollment
 * fetch (for the program summary + the awaiting_community guard), the owned-
 * community selection, the contract-acceptance toggle, and the submit. On
 * success it routes back to the §6.11 detail, where the now-active mentoring
 * (community attached, windows started) is shown.
 */
export default function useAttachCommunity(enrollmentId) {
  const navigate = useNavigate();
  const { communities, loadCommunities } = useCommunity();

  const [enrollment, setEnrollment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [selectedId, setSelectedId] = useState(null);
  const [contractAccepted, setContractAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const requestRef = useRef(0);

  // Owned communities are the only valid attach targets — the server rejects
  // anything else with a 403 (community not owned, §6.10).
  const ownedCommunities = useMemo(
    () => (communities ?? []).filter((c) => c.role === "owner"),
    [communities],
  );

  useEffect(() => {
    if (!enrollmentId) return;

    const requestId = ++requestRef.current;
    setLoading(true);
    setNotFound(false);

    (async () => {
      const res = await api.get(`mentoring/my-enrollments/${enrollmentId}`);
      if (requestId !== requestRef.current) return;

      if (res?.response) {
        setEnrollment(null);
        setNotFound(true);
      } else {
        const detail = adaptMyEnrollmentDetail(res?.data);
        setEnrollment(detail);
        setNotFound(!detail);
      }
      setLoading(false);
    })();
  }, [enrollmentId]);

  const isAwaitingCommunity = enrollment?.status === "awaiting_community";
  const canSubmit =
    !!selectedId && contractAccepted && isAwaitingCommunity && !submitting;

  const submit = useCallback(async () => {
    if (!selectedId) {
      window.setSnackbar?.("Selecione uma comunidade para continuar.");
      return;
    }
    if (!contractAccepted) {
      window.setSnackbar?.("Aceite o contrato do programa para continuar.");
      return;
    }

    setSubmitting(true);

    const payload = { community_id: selectedId, contract_accepted: true };

    const res = await api.post(
      `mentoring/enrollments/${enrollmentId}/community`,
      payload,
    );

    if (res?.response) {
      const { status, data } = res.response;
      if (status === 403) {
        window.setSnackbar?.("Você não é proprietário desta comunidade.");
      } else {
        window.setSnackbar?.(
          data?.message ?? "Não foi possível vincular a comunidade. Tente novamente.",
        );
      }
      setSubmitting(false);
      return;
    }

    window.setSnackbar?.("Comunidade vinculada — mentoria ativa");
    navigate(`/mentoring/${enrollmentId}`);
  }, [selectedId, contractAccepted, enrollmentId, navigate]);

  const openCreate = useCallback(() => setCreateOpen(true), []);
  const closeCreate = useCallback(() => setCreateOpen(false), []);

  // "Criar nova comunidade" → CreateCommunityModal (redirectOnSuccess=false so it
  // stays here). Refresh the community list, then pre-select the new community.
  const onCommunityCreated = useCallback(
    async (created) => {
      if (created?.id) setSelectedId(created.id);
      await loadCommunities?.();
    },
    [loadCommunities],
  );

  return {
    enrollment,
    loading,
    notFound,
    isAwaitingCommunity,
    ownedCommunities,
    selectedId,
    setSelectedId,
    contractAccepted,
    setContractAccepted,
    submitting,
    canSubmit,
    submit,
    createOpen,
    openCreate,
    closeCreate,
    onCommunityCreated,
  };
}
